import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  FaWhatsapp,
  FaUser,
  FaPaperPlane,
  FaClock,
  FaExclamationTriangle,
  FaSearch,
  FaSync,
  FaCommentDots,
  FaLock,
  FaCheckDouble,
  FaTimes,
  FaFileAlt,
  FaCircle,
  FaShieldAlt
} from "react-icons/fa";
import { toast } from "react-toastify";

// Helper to format timestamps relative or readable
const formatTime = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";

  const now = new Date();
  const diffMinutes = Math.floor((now - date) / (1000 * 60));
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMinutes < 1) return "Just now";
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString("en-IN", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

// Check if 24 hours have passed since last incoming message time
const calculate24hExpired = (lastTimeIso, windowActiveProp) => {
  if (windowActiveProp === false) return true;
  if (windowActiveProp === true) return false;
  if (!lastTimeIso) return true;

  const date = new Date(lastTimeIso);
  if (isNaN(date.getTime())) return true;

  const hoursDiff = (new Date() - date) / (1000 * 60 * 60);
  return hoursDiff > 24;
};

// Available WhatsApp Quick Templates
const DEFAULT_TEMPLATES = [
  {
    id: "order_confirmation",
    name: "Order Confirmation",
    category: "UTILITY",
    description: "Send order status confirmation to customer",
    templateName: "order_confirmation_v1",
    languageCode: "en",
    text: "Hello! Your order with {{business_name}} has been confirmed and is being processed.",
    variables: ["business_name"]
  },
  {
    id: "payment_reminder",
    name: "Payment Reminder",
    category: "UTILITY",
    description: "Remind customer about unpaid balance/invoice",
    templateName: "payment_reminder_v1",
    languageCode: "en",
    text: "Hi! This is a friendly reminder regarding your pending payment of {{amount}} for invoice {{invoice_no}}.",
    variables: ["amount", "invoice_no"]
  },
  {
    id: "shipping_update",
    name: "Shipping & Delivery Update",
    category: "UTILITY",
    description: "Notify customer about tracking and dispatch",
    templateName: "shipping_update_v1",
    languageCode: "en",
    text: "Great news! Your order {{order_id}} has been shipped via {{courier}}. Tracking link: {{link}}.",
    variables: ["order_id", "courier", "link"]
  },
  {
    id: "customer_support",
    name: "Customer Support Greeting",
    category: "SERVICE",
    description: "Re-open customer conversation with support prompt",
    templateName: "support_greeting_v1",
    languageCode: "en",
    text: "Hello! We noticed your inquiry. How can our team assist you today?",
    variables: []
  }
];

export default function SellerInbox({ merchantId: propMerchantId }) {
  const params = useParams();
  // Determine Merchant ID (prop -> URL param -> localStorage -> default fallback)
  const merchantId =
    propMerchantId ||
    params?.bussinessUuid ||
    localStorage.getItem("bussinessUuid") ||
    localStorage.getItem("merchantId") ||
    localStorage.getItem("bussinessId") ||
    "DEFAULT_MERCHANT";

  // State Management
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [windowActive, setWindowActive] = useState(true);
  const [lastMessageTime, setLastMessageTime] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  const [messageText, setMessageText] = useState("");
  
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingChat, setLoadingChat] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Template Modal State
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(DEFAULT_TEMPLATES[0]);
  const [templateInputs, setTemplateInputs] = useState({});
  const [sendingTemplate, setSendingTemplate] = useState(false);

  const chatEndRef = useRef(null);

  // Helper for API calls with fallback paths
  const fetchApi = async (pathPrimary, pathFallback, options = {}) => {
    const AUTH_TOKEN = localStorage.getItem("token") || "";
    const headers = {
      "x-auth-token": AUTH_TOKEN,
      "Content-Type": "application/json",
      ...options.headers
    };

    try {
      const res = await axios({ url: pathPrimary, headers, ...options });
      return res.data;
    } catch (err1) {
      if (pathFallback) {
        try {
          const res2 = await axios({ url: pathFallback, headers, ...options });
          return res2.data;
        } catch (err2) {
          throw err2;
        }
      }
      throw err1;
    }
  };

  // Fetch Customer Inbox List
  const fetchCustomers = async (isSilent = false) => {
    if (!isSilent) setLoadingCustomers(true);
    try {
      const primaryUrl = `/api/inbox/${merchantId}`;
      const fallbackUrl = `/api/v1/whatsapp/inbox/${merchantId}`;
      
      const resData = await fetchApi(primaryUrl, fallbackUrl, { method: "GET" });
      if (resData && resData.success && Array.isArray(resData.data)) {
        setCustomers(resData.data);
        // If no customer selected yet, select first
        if (!selectedCustomer && resData.data.length > 0) {
          setSelectedCustomer(resData.data[0]);
        }
      }
    } catch (error) {
      console.error("Error fetching inbox customer list:", error);
    } finally {
      if (!isSilent) setLoadingCustomers(false);
    }
  };

  // Fetch Chat History for Selected Customer
  const fetchChatHistory = async (customerPhone, isSilent = false) => {
    if (!customerPhone) return;
    if (!isSilent) setLoadingChat(true);

    try {
      const primaryUrl = `/api/inbox/${merchantId}/${customerPhone}`;
      const fallbackUrl = `/api/v1/whatsapp/inbox/${merchantId}/${customerPhone}`;

      const resData = await fetchApi(primaryUrl, fallbackUrl, { method: "GET" });

      if (resData && resData.success) {
        setChatHistory(resData.data || []);
        if (typeof resData.windowActive === "boolean") {
          setWindowActive(resData.windowActive);
        }
        if (resData.last_message_time || resData.lastInMessageTime) {
          setLastMessageTime(resData.last_message_time || resData.lastInMessageTime);
        }
        
        // Mark as read
        try {
          await fetchApi("/api/inbox/read", "/api/v1/whatsapp/inbox/read", {
            method: "PUT",
            data: { bussinessUuid: merchantId, customer_phone: customerPhone }
          });
        } catch (e) {
          // ignore read error
        }
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
    } finally {
      if (!isSilent) setLoadingChat(false);
    }
  };

  // Initial Load & Polling Interval
  useEffect(() => {
    fetchCustomers();
    const interval = setInterval(() => {
      fetchCustomers(true);
      if (selectedCustomer?.customer_phone || selectedCustomer?._id) {
        const phone = selectedCustomer.customer_phone || selectedCustomer._id;
        fetchChatHistory(phone, true);
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [merchantId]);

  // Handle Customer Selection
  useEffect(() => {
    if (selectedCustomer) {
      const phone = selectedCustomer.customer_phone || selectedCustomer._id;
      setLastMessageTime(selectedCustomer.last_message_time || selectedCustomer.lastMessageTime);
      fetchChatHistory(phone);
    }
  }, [selectedCustomer]);

  // Auto-scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, loadingChat]);

  // Check 24h Expiration status
  const currentCustomerLastTime =
    lastMessageTime || selectedCustomer?.last_message_time || selectedCustomer?.lastMessageTime;
  const is24hExpired = calculate24hExpired(currentCustomerLastTime, windowActive);

  // Send Direct Reply (Only available when window is active <= 24h)
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() || is24hExpired || sendingMessage) return;

    const phone = selectedCustomer?.customer_phone || selectedCustomer?._id;
    if (!phone) return;

    setSendingMessage(true);
    try {
      const primaryUrl = "/api/inbox/send-reply";
      const fallbackUrl = "/api/v1/whatsapp/send-reply";

      const resData = await fetchApi(primaryUrl, fallbackUrl, {
        method: "POST",
        data: {
          to: phone,
          message: messageText,
          bussinessUuid: merchantId
        }
      });

      if (resData.success) {
        toast.success("Message sent successfully!");
        setMessageText("");
        fetchChatHistory(phone, true);
        fetchCustomers(true);
      } else {
        toast.error(resData.message || "Failed to send message.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(error.response?.data?.message || "Failed to send message. 24h window might have lapsed.");
    } finally {
      setSendingMessage(false);
    }
  };

  // Send WhatsApp Template Message
  const handleSendTemplate = async () => {
    const phone = selectedCustomer?.customer_phone || selectedCustomer?._id;
    if (!phone || !selectedTemplate) return;

    setSendingTemplate(true);
    try {
      const primaryUrl = "/api/v1/whatsapp/send-template";
      const fallbackUrl = "/api/inbox/send-template";

      // Map template variables to parameters array format
      const components = selectedTemplate.variables.length > 0 ? [
        {
          type: "body",
          parameters: selectedTemplate.variables.map(varName => ({
            type: "text",
            text: templateInputs[varName] || `[${varName}]`
          }))
        }
      ] : [];

      const resData = await fetchApi(primaryUrl, fallbackUrl, {
        method: "POST",
        data: {
          to: phone,
          templateName: selectedTemplate.templateName,
          languageCode: selectedTemplate.languageCode || "en",
          components,
          bussinessUuid: merchantId
        }
      });

      if (resData.success) {
        toast.success(`Template "${selectedTemplate.name}" sent to ${phone}!`);
        setShowTemplateModal(false);
        setTemplateInputs({});
        fetchChatHistory(phone, true);
        fetchCustomers(true);
      } else {
        toast.error(resData.message || "Failed to send template.");
      }
    } catch (error) {
      console.error("Error sending template:", error);
      toast.error(error.response?.data?.message || "Error sending template message.");
    } finally {
      setSendingTemplate(false);
    }
  };

  // Filtered customer list by search term
  const filteredCustomers = customers.filter((c) => {
    const phone = c.customer_phone || c._id || "";
    const msg = c.lastMessage || "";
    return (
      phone.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] max-w-7xl mx-auto p-2 sm:p-4 bg-slate-50 dark:bg-slate-900 text-slate-800 dark:text-slate-100 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
      
      {/* Top Header Bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 rounded-t-xl">
        <div className="flex items-center space-x-3">
          <div className="p-2.5 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 rounded-xl">
            <FaWhatsapp className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              WhatsApp Business Inbox
              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-medium">
                Live Sync
              </span>
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Merchant ID: <code className="bg-slate-100 dark:bg-slate-700 px-1.5 py-0.5 rounded text-slate-700 dark:text-slate-300 font-mono">{merchantId}</code>
            </p>
          </div>
        </div>

        <button
          onClick={() => fetchCustomers()}
          title="Refresh Inbox"
          className="flex items-center space-x-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-emerald-600 dark:hover:text-emerald-400 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition"
        >
          <FaSync className={`w-3.5 h-3.5 ${loadingCustomers ? "animate-spin" : ""}`} />
          <span className="hidden sm:inline">Refresh</span>
        </button>
      </div>

      {/* Dual Pane Container */}
      <div className="flex flex-1 overflow-hidden bg-slate-100/50 dark:bg-slate-900/50">

        {/* LEFT PANE: Customer Conversation List */}
        <div className="w-full md:w-80 lg:w-96 flex flex-col border-r border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          
          {/* Search Box */}
          <div className="p-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/80">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-slate-400 dark:text-slate-500 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search customer phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-8 py-2 text-xs bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 transition"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                >
                  <FaTimes className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Customer List Scroll Container */}
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800/60">
            {loadingCustomers && customers.length === 0 ? (
              <div className="p-6 text-center text-slate-400 dark:text-slate-500 space-y-3">
                <div className="inline-block animate-spin text-emerald-500 text-2xl">⌛</div>
                <p className="text-xs">Loading conversations...</p>
              </div>
            ) : filteredCustomers.length === 0 ? (
              <div className="p-6 m-3 text-center space-y-2 bg-slate-50/80 dark:bg-slate-950/50 rounded-xl border border-dashed border-slate-200 dark:border-slate-800">
                <FaCommentDots className="w-8 h-8 mx-auto text-slate-400 dark:text-slate-600" />
                <p className="text-sm font-bold text-slate-800 dark:text-slate-200">No customers found</p>
                <p className="text-xs text-slate-500 dark:text-slate-400">Incoming customer chats will appear here.</p>
              </div>
            ) : (
              filteredCustomers.map((customer) => {
                const phone = customer.customer_phone || customer._id;
                const isSelected = selectedCustomer && (selectedCustomer.customer_phone === phone || selectedCustomer._id === phone);
                const hasUnread = customer.unreadCount > 0;
                const timeText = formatTime(customer.last_message_time || customer.lastMessageTime);

                return (
                  <div
                    key={phone}
                    onClick={() => setSelectedCustomer(customer)}
                    className={`flex items-center space-x-3 p-3.5 cursor-pointer transition-all border-l-4 ${
                      isSelected
                        ? "bg-emerald-50/80 dark:bg-emerald-950/40 border-emerald-500 text-slate-900 dark:text-white"
                        : "border-transparent hover:bg-slate-50 dark:hover:bg-slate-800/50 text-slate-700 dark:text-slate-300"
                    }`}
                  >
                    {/* Customer Avatar */}
                    <div className="relative flex-shrink-0">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-emerald-500 to-teal-600 text-white flex items-center justify-center font-bold text-sm shadow-sm">
                        <FaUser className="w-4 h-4 opacity-90" />
                      </div>
                      {hasUnread && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center rounded-full ring-2 ring-white dark:ring-slate-900 animate-pulse">
                          {customer.unreadCount}
                        </span>
                      )}
                    </div>

                    {/* Customer Info Snippet */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h2 className="text-xs font-bold truncate text-slate-900 dark:text-slate-100">
                          {phone}
                        </h2>
                        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 flex-shrink-0 ml-1">
                          {timeText}
                        </span>
                      </div>
                      <p className={`text-xs truncate ${hasUnread ? "font-semibold text-slate-900 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"}`}>
                        {customer.lastMessage || "No messages yet"}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* RIGHT PANE: Chat History Window & 24h Policy Controls */}
        <div className="flex-1 flex flex-col bg-slate-50/80 dark:bg-slate-900">
          
          {selectedCustomer ? (
            <>
              {/* Chat Window Header */}
              <div className="flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm">
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-xs">
                    <FaUser className="w-4 h-4" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-slate-900 dark:text-white">
                      {selectedCustomer.customer_phone || selectedCustomer._id}
                    </h2>
                    <p className="text-[11px] text-slate-400 flex items-center space-x-1">
                      <FaClock className="w-3 h-3 text-slate-400" />
                      <span>Last customer activity: {formatTime(currentCustomerLastTime) || "Unknown"}</span>
                    </p>
                  </div>
                </div>

                {/* 24-Hour Policy Window Status Badge */}
                <div>
                  {is24hExpired ? (
                    <div className="flex items-center space-x-1.5 px-3 py-1 bg-amber-500/10 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400 border border-amber-500/20 rounded-full text-xs font-semibold">
                      <FaCircle className="w-2 h-2 text-amber-500 animate-ping" />
                      <FaLock className="w-3 h-3" />
                      <span>24h Expired (Template Required)</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1.5 px-3 py-1 bg-emerald-500/10 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400 border border-emerald-500/20 rounded-full text-xs font-semibold">
                      <FaCircle className="w-2 h-2 text-emerald-500" />
                      <FaShieldAlt className="w-3 h-3" />
                      <span>24h Session Active</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Chat Message Scroll Container */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-100/60 dark:bg-slate-950/50">
                {loadingChat && chatHistory.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-slate-400 space-x-2">
                    <div className="animate-spin text-emerald-500 text-xl">⏳</div>
                    <span className="text-xs">Loading chat history...</span>
                  </div>
                ) : chatHistory.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-400 space-y-2">
                    <FaCommentDots className="w-10 h-10 text-slate-300 dark:text-slate-700" />
                    <p className="text-xs">No prior conversation history with this customer.</p>
                  </div>
                ) : (
                  chatHistory.map((msg, idx) => {
                    const isOutgoing = msg.direction === "OUT";
                    const isTemplate = msg.templateName || msg.message_text?.startsWith("[Template]");

                    return (
                      <div
                        key={msg._id || idx}
                        className={`flex flex-col ${isOutgoing ? "items-end" : "items-start"}`}
                      >
                        <div
                          className={`max-w-[85%] sm:max-w-[70%] px-3.5 py-2.5 rounded-2xl shadow-sm text-xs leading-relaxed ${
                            isOutgoing
                              ? "bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-br-none"
                              : "bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-bl-none border border-slate-200/80 dark:border-slate-700"
                          }`}
                        >
                          {isTemplate && (
                            <div className="flex items-center space-x-1.5 mb-1 pb-1 border-b border-emerald-400/30 text-[10px] font-bold uppercase tracking-wider text-emerald-100">
                              <FaFileAlt className="w-3 h-3" />
                              <span>WhatsApp Template</span>
                            </div>
                          )}
                          <p className="whitespace-pre-wrap break-words">{msg.message_text}</p>
                          
                          <div className={`flex items-center justify-end space-x-1 mt-1 text-[10px] ${isOutgoing ? "text-emerald-100/80" : "text-slate-400"}`}>
                            <span>{formatTime(msg.timestamp || msg.createdAt)}</span>
                            {isOutgoing && <FaCheckDouble className="w-3 h-3 ml-0.5 text-emerald-200" />}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={chatEndRef} />
              </div>

              {/* 24-HOUR POLICY WARNING BANNER */}
              {is24hExpired && (
                <div className="px-4 py-2.5 bg-amber-500/10 dark:bg-amber-950/40 border-t border-b border-amber-500/20 flex flex-col sm:flex-row items-center justify-between gap-2">
                  <div className="flex items-center space-x-2 text-amber-700 dark:text-amber-300 text-xs">
                    <FaExclamationTriangle className="w-4 h-4 flex-shrink-0 text-amber-500" />
                    <span>
                      <strong>24-Hour Policy Window Expired:</strong> Free-form text messages are disabled. You must send a pre-approved template message to contact this customer.
                    </span>
                  </div>
                  
                  <button
                    onClick={() => setShowTemplateModal(true)}
                    className="flex items-center space-x-1.5 px-4 py-1.5 bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white font-bold text-xs rounded-lg shadow-md transition transform hover:scale-105 flex-shrink-0"
                  >
                    <FaFileAlt className="w-3.5 h-3.5" />
                    <span>Use Template</span>
                  </button>
                </div>
              )}

              {/* INPUT BAR & SEND BUTTON */}
              <div className="p-3 bg-white dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
                <form onSubmit={handleSendMessage} className="flex items-center space-x-2">
                  
                  <input
                    type="text"
                    disabled={is24hExpired || sendingMessage}
                    placeholder={
                      is24hExpired
                        ? "24-hour window expired. Click 'Use Template' to message..."
                        : "Type a customer reply message..."
                    }
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    className={`flex-1 px-4 py-2.5 text-xs rounded-xl border focus:outline-none transition ${
                      is24hExpired
                        ? "bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-400 cursor-not-allowed"
                        : "bg-slate-50 dark:bg-slate-900 border-slate-200 dark:border-slate-700 focus:ring-2 focus:ring-emerald-500/50 text-slate-800 dark:text-slate-100"
                    }`}
                  />

                  {/* If 24h expired, display "Use Template" button right next to input */}
                  {is24hExpired ? (
                    <button
                      type="button"
                      onClick={() => setShowTemplateModal(true)}
                      className="px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold text-xs rounded-xl shadow transition flex items-center space-x-1.5 whitespace-nowrap"
                    >
                      <FaFileAlt className="w-3.5 h-3.5" />
                      <span>Use Template</span>
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={!messageText.trim() || sendingMessage}
                      className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-xs rounded-xl shadow transition flex items-center space-x-1.5 whitespace-nowrap"
                    >
                      <FaPaperPlane className={`w-3.5 h-3.5 ${sendingMessage ? "animate-bounce" : ""}`} />
                      <span>Send</span>
                    </button>
                  )}
                </form>
              </div>
            </>
          ) : (
            /* No Customer Selected View */
            <div className="flex flex-col items-center justify-center h-full text-slate-400 dark:text-slate-500 p-8 text-center space-y-3 bg-slate-50/40 dark:bg-slate-950/40">
              <div className="p-4 bg-emerald-500/10 text-emerald-500 dark:text-emerald-400 rounded-full">
                <FaWhatsapp className="w-12 h-12" />
              </div>
              <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                Select a Customer Conversation
              </h3>
              <p className="text-xs max-w-sm text-slate-500 dark:text-slate-400">
                Choose a customer from the left list to view their WhatsApp chat history, manage replies, or send pre-approved template messages.
              </p>
            </div>
          )}
        </div>

      </div>

      {/* TEMPLATE SELECTION MODAL */}
      {showTemplateModal && (
        <div className="fixed inset-0 z-50 bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-in fade-in zoom-in duration-200">
            
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-4 bg-slate-50 dark:bg-slate-800/80 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center space-x-2 text-emerald-600 dark:text-emerald-400">
                <FaFileAlt className="w-5 h-5" />
                <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                  Select WhatsApp Template
                </h3>
              </div>
              <button
                onClick={() => setShowTemplateModal(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Since the 24-hour response window has expired, pick a pre-approved template to re-engage with customer{" "}
                <strong className="text-slate-800 dark:text-slate-200">
                  {selectedCustomer?.customer_phone || selectedCustomer?._id}
                </strong>:
              </p>

              {/* Template Cards List */}
              <div className="space-y-2.5">
                {DEFAULT_TEMPLATES.map((tmpl) => {
                  const isSelected = selectedTemplate.id === tmpl.id;
                  return (
                    <div
                      key={tmpl.id}
                      onClick={() => {
                        setSelectedTemplate(tmpl);
                        setTemplateInputs({});
                      }}
                      className={`p-3.5 rounded-xl border cursor-pointer transition ${
                        isSelected
                          ? "border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/40 ring-1 ring-emerald-500"
                          : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 bg-white dark:bg-slate-800"
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold text-slate-900 dark:text-white">
                          {tmpl.name}
                        </span>
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                          {tmpl.category}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed mb-2">
                        {tmpl.text}
                      </p>
                    </div>
                  );
                })}
              </div>

              {/* Dynamic Variables Inputs */}
              {selectedTemplate && selectedTemplate.variables.length > 0 && (
                <div className="p-3.5 bg-slate-50 dark:bg-slate-800/80 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2.5">
                  <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300">
                    Template Variables
                  </h4>
                  {selectedTemplate.variables.map((varName) => (
                    <div key={varName}>
                      <label className="block text-[11px] font-medium text-slate-600 dark:text-slate-400 mb-1 capitalize">
                        {varName.replace("_", " ")}
                      </label>
                      <input
                        type="text"
                        placeholder={`Enter ${varName}...`}
                        value={templateInputs[varName] || ""}
                        onChange={(e) =>
                          setTemplateInputs({ ...templateInputs, [varName]: e.target.value })
                        }
                        className="w-full px-3 py-1.5 text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end space-x-3 px-5 py-3.5 bg-slate-50 dark:bg-slate-800 border-t border-slate-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setShowTemplateModal(false)}
                className="px-4 py-2 text-xs font-medium text-slate-600 dark:text-slate-300 hover:text-slate-800"
              >
                Cancel
              </button>

              <button
                type="button"
                disabled={sendingTemplate}
                onClick={handleSendTemplate}
                className="flex items-center space-x-1.5 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow transition"
              >
                <FaPaperPlane className={`w-3.5 h-3.5 ${sendingTemplate ? "animate-spin" : ""}`} />
                <span>{sendingTemplate ? "Sending..." : "Send Template"}</span>
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}
